/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import abstractCaseTranslations from "./abstractCaseTranslations";
import abstractLitigationCaseTranslations from "./abstractLitigationCaseTranslations";
import organizationTranslations from "./organizationTranslations";
import personTranslations from "./personTranslations";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import addressTranslations from "./addressTranslations";
import * as applicationStatuses from "@/src/services/contractLaw/applicationStatus";
import organizationEmailTranslations from "./organizationEmailTranslations";

const abstractContractClaimTranslations = {
  ...abstractCaseTranslations,
  ...abstractLitigationCaseTranslations,
  caseSelection: {
    question: (caseData: AbstractCase) => {
      if (caseData.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION) {
        return "Wir würden Ihnen folgende Vertragsart vorschlagen:";
      }

      return "Um welche Art von Vertrag handelt es sich?";
    },
  },
  personalMessage: {
    stepHeadline: (caseData: AbstractCase) => {
      if (caseData.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION) {
        return "Schilderung";
      }

      return "Ihr Vertrag";
    },
    headline: (caseData: AbstractCase) => {
      if (caseData.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION) {
        return "Schilderung Ihres Anliegens";
      }

      return "Beschreibung der Situation";
    },
    mainDescription: (caseData: AbstractCase) => {
      if (caseData.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION) {
        return "Bitte schildern Sie uns hier kurz Ihr Anliegen. Wir werden Ihnen dabei helfen, die passende Vertragsart zu finden. Sie werden zu einem späteren Zeitpunkt die Möglichkeit haben, weitere Details hinzuzufügen.";
      }

      return "Bitte schildern Sie uns hier kurz Ihre Situation und Fragen. Jede relevante Information hilft uns, Ihren Fall besser bearbeiten zu können. Im nächsten Schritt können Sie - wenn gewünscht - noch ergänzende Dokumente hochladen.";
    },
    label: (caseData: AbstractCase) => {
      if (caseData.applicationStatus === applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION) {
        return "Ihr Anliegen";
      }

      return "Ihre Situation und Fragen";
    },
  },
  buyerPaidStatus: {
    label: "Kaufpreis bezahlt?",
    question: "Wurde der Kaufpreis vollständig gezahlt?",
    options: {
      fully: {
        label: "Ja, vollständig bezahlt",
      },
      partially: {
        label: "Nein, bisher wurde nur eine Anzahlung geleistet",
      },
      none: {
        label: "Nein, es wurde noch nichts gezahlt",
      },
    },
  },
  contractDate: {
    label: "Datum des Vertrags",
  },
  subjectOfDispute: {
    label: "Forderung",
    question: "Was fordert Ihr Vertragspartner von Ihnen?",
    options: {
      money: {
        label: "Geld",
      },
      goods: {
        label: "Ware/Leistung",
      },
      other: {
        label: "Ich fordere etwas ganz anderes",
      },
    },
  },
  claimedAmountClient: {
    label: "Forderung in €",
    question: (caseData: AbstractCase) =>
      (["reminder", "debtCollection"].includes(caseData.defenseProcessStatus)
        ? "Wie viel Geld fordert Ihr Vertragspartner (ohne Mahn-/Inkassogebühren)?"
        : "Wie viel Geld fordert Ihr Vertragspartner") +
      " Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
    helperText:
      "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00. Wenn Sie den Betrag noch nicht genau wissen, lassen Sie das Feld gerne einfach leer",
  },
  objectiveClient: {
    label: "Ziel",
    options: {
      fulfillment: {
        label: "Erfüllung des Vertrags",
      },
      withdrawal: {
        label: "Rücktritt vom Vertrag",
      },
      replacement: {
        label: "Ersatz oder Austausch",
      },
      repair: {
        label: "Nachbesserung oder Reparatur",
      },
      reduction: {
        label: "Volle oder teilweise Erstattung",
      },
      compensation: {
        label: "Schadensersatz",
      },
      other: {
        label: "Andere Zielsetzung",
      },
    },
  },
  debtCollectionAgency: {
    organizationEmail: organizationEmailTranslations,
    organization: {
      ...organizationTranslations,
    },
  },
  debtCollectionAgencyReference: {
    label: "Aktenzeichen Inkassobüro",
  },
  personalTextMessage: {
    label: "Ihre Situation und Fragen",
  },
  finalClientDescriptionAndObjectives: {
    label: "Beschreibung der Situation",
  },
  additionalClients: {
    0: personTranslations,
    1: personTranslations,
    2: personTranslations,
    3: personTranslations,
    4: personTranslations,
    5: personTranslations,
    6: personTranslations,
    7: personTranslations,
    8: personTranslations,
    9: personTranslations,
  },
  opponentLawFirm: {
    organizationEmail: organizationEmailTranslations,
    organization: {
      ...organizationTranslations,
      name: { label: "Name der Kanzlei" },
      address: {
        ...addressTranslations,
        headline: "Welche Kontaktdaten des Anwalts/der Anwaltskanzlei liegen Ihnen vor? Diese finden Sie zum Beispiel auf dem Schreiben im Briefkopf.",
      },
    },
  },
};

export default abstractContractClaimTranslations;
